import { Ppe } from '../types/project';

/**
 * PPE 셈 — admin PPE 「새 프로젝트 등록」(GRK_admin NewProjectModal `totals`)과 같은 식 · 같은 단위(만원).
 *
 *   Revenue         = Σ 입금 일정 금액(by Schedule)
 *   투입인건비      = Σ round(M/M × 인력원가)
 *   CoGs(프리랜서)  = 프리랜서 round(M/M × 단가) + Sales Brokerage Fee
 *   Cost of Finance = round(투입인건비 × costOfFinanceRate)        — Indirect Opex
 *   Project mgmt    = round(Revenue × projectManagementRate)       — Direct Opex
 *   Gross Margin    = Revenue − CoGs(프리랜서) − 투입인건비
 *   Operation       = Gross Margin − Indirect − Direct
 *   Income Tax      = Operation × 20% · Net Profit = Operation − Tax
 *   ECM             = round(Gross Margin × 0.45 / 1.45) · Reserved = ceil((CoF + ECM) / 100) × 100
 */

export const COF_NAME = 'Cost of Finance';
export const PM_NAME = 'Project management';
export const isCof = (n: string) => n.includes(COF_NAME);
export const isPm = (n: string) => n.includes(PM_NAME);
export const isFreelancer = (n: string) => n.includes('프리랜서 고급') || n.includes('프리랜서 중급');

/** admin createInitialState 와 같다 — 프리랜서 고급 1,800 · 중급 1,500 만원/MM, CoF 10% · PM 3% */
export function emptyPpe(): Ppe {
  return {
    startDate: '', endDate: '', months: 1,
    revenueItems: [
      { name: '착수금', ratio: 0, amount: 0, dueDate: '' },
      { name: '중도금', ratio: 0, amount: 0, dueDate: '' },
      { name: '잔금', ratio: 0, amount: 0, dueDate: '' },
    ],
    manpowerItems: [],
    grossCostItems: [
      { name: 'Sales Brokerage Fee', amount: 0 },
      { name: '프리랜서 고급', unitPrice: 1800, mm: 0, amount: 0 },
      { name: '프리랜서 중급', unitPrice: 1500, mm: 0, amount: 0 },
    ],
    indirectOpexItems: [
      { name: 'Rent & Overhead', amount: 0 },
      { name: `${COF_NAME} (10%)`, amount: 0 },
    ],
    directOpexItems: [
      { name: `${PM_NAME} (3%)`, amount: 0 },
      { name: 'ENS', amount: 0 },
      { name: 'Print & Paper', amount: 0 },
      { name: 'Conference', amount: 0 },
      { name: 'Travel for business', amount: 0 },
    ],
    costOfFinanceRate: 0.1,
    projectManagementRate: 0.03,
    hrYear: new Date().getFullYear(),
  };
}

export interface PpeTotals {
  revenue: number;
  manpower: number;
  cogs: number;
  grossMargin: number;
  costOfFinance: number;
  projectManagement: number;
  indirect: number;
  direct: number;
  operation: number;
  tax: number;
  net: number;
  ecm: number;
  reserved: number;
}

export const freelancerAmount = (x: { name: string; unitPrice?: number; mm?: number; amount: number }) =>
  isFreelancer(x.name) ? Math.round((x.mm ?? 0) * (x.unitPrice ?? (x.name.includes('고급') ? 1800 : 1500))) : x.amount;

export function ppeTotals(p: Ppe): PpeTotals {
  const revenue = p.revenueItems.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const manpower = p.manpowerItems.reduce((s, r) => s + Math.round((r.mm || 0) * (r.unitCost || 0)), 0);
  const cogs = p.grossCostItems.reduce((s, x) => s + freelancerAmount(x), 0);
  const costOfFinance = Math.round(manpower * p.costOfFinanceRate);
  const projectManagement = Math.round(revenue * p.projectManagementRate);
  const indirect = p.indirectOpexItems.reduce((s, x) => s + (isCof(x.name) ? costOfFinance : x.amount || 0), 0);
  const direct = p.directOpexItems.reduce((s, x) => s + (isPm(x.name) ? projectManagement : x.amount || 0), 0);
  const grossMargin = revenue - cogs - manpower;
  const operation = grossMargin - indirect - direct;
  const tax = operation * 0.2;
  const ecm = Math.round(grossMargin * (0.45 / 1.45));
  return {
    revenue, manpower, cogs, grossMargin, costOfFinance, projectManagement, indirect, direct, operation, tax, net: operation - tax,
    ecm, reserved: Math.ceil((costOfFinance + ecm) / 100) * 100,
  };
}

/** 기간 → 개월 수(admin 과 같이 달 차이 + 1) */
export function monthsBetween(start: string, end: string): number {
  if (!start || !end) return 1;
  const a = new Date(start), b = new Date(end);
  return Math.max(1, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1);
}

/** 저장 전에 자동 칸(비율 · 프리랜서 금액 · CoF · PM · 개월 · 원가 연도)을 채운다 — admin 에 넘길 때 그대로 쓰게 */
export function settlePpe(p: Ppe): Ppe {
  const t = ppeTotals(p);
  return {
    ...p,
    months: monthsBetween(p.startDate, p.endDate),
    hrYear: p.startDate ? new Date(p.startDate).getFullYear() : p.hrYear,
    revenueItems: p.revenueItems.map((x) => ({ ...x, ratio: t.revenue > 0 ? x.amount / t.revenue : 0 })),
    grossCostItems: p.grossCostItems.map((x) => ({ ...x, amount: freelancerAmount(x) })),
    indirectOpexItems: p.indirectOpexItems.map((x) => (isCof(x.name) ? { ...x, amount: t.costOfFinance } : x)),
    directOpexItems: p.directOpexItems.map((x) => (isPm(x.name) ? { ...x, amount: t.projectManagement } : x)),
  };
}
